import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const exportUsersCsvGuide: AdminGuide = {
  id: "export-users-csv",
  title: "Export users to CSV (segments + privacy rules)",
  category: "Other",
  audience: ["Support", "Ops", "Finance", "CS"],
  summary:
    "Export a targeted customer segment from the Users page for internal analysis or outreach, without oversharing personal data.",
  keywords: [
    "csv",
    "export",
    "users",
    "segments",
    "active",
    "inactive",
    "trial",
    "never logged in",
    "cancelled",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.CSV_EXPORT_FEATURE,
    GUIDE_SOURCE_DOCS.USAGE_TRACKING,
  ],
  warnings: [
    "Exports can include personal data (names, emails, phone numbers). Only export what you need and store it safely.",
    "Do not upload exported CSVs to unapproved tools or share them in public Slack channels.",
  ],
  safetyNotes: [
    "If you only need counts or high-level trends, prefer Admin dashboards instead of exporting raw user lists.",
    "If you plan to contact customers, follow internal comms policies (unsubscribe handling, correct sender, and approved copy).",
  ],
  whatThisHelpsYouDo: [
    "Export a specific user segment (active, inactive, never logged in, trial, etc.).",
    "Understand what fields are included and how to handle them safely.",
    "Use CSV exports as inputs for internal workflows (analysis, outreach planning).",
  ],
  whenToUse: [
    "You need a list of users for an internal campaign or analysis (e.g., re-engagement).",
    "You need to share a targeted list with an internal teammate (minimum necessary).",
  ],
  whenNotToUse: [
    "You don’t have a clear purpose or approval to handle personal data.",
    "You need a one-off customer communication (consider direct support outreach instead of bulk export).",
  ],
  beforeYouStart: [
    "Know which segment you actually need (avoid “export everything” by default).",
    "Have an approved internal place to store the export (and a plan to delete it when done).",
  ],
  steps: [
    "Open Admin → **Users** (`/users`).",
    "Click **Export CSV** in the toolbar.",
    "Choose the segment that matches your goal (for example: Active Subscribers, Trial Users, Never Logged In).",
    "Review the confirmation dialog (user count + included fields).",
    "Confirm the export and download the CSV file.",
    "Store the file in an approved internal location and delete it when the task is complete.",
  ],
  checklists: [
    {
      title: "Privacy checklist",
      items: [
        "I exported the smallest segment that meets the goal (not “all users”).",
        "I stored the file in an approved internal location.",
        "I will delete the file when it is no longer needed.",
        "I will not paste customer emails/phone numbers into tickets or public channels.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "You produced a CSV containing only the needed segment.",
    "The CSV is stored safely and shared only with internal stakeholders who need it.",
  ],
  troubleshooting: [
    {
      title: "The user count is unexpected",
      body: [
        "Re-check which segment you selected.",
        "Remember that “activity” segments are based on usage-derived activity status (not just account creation).",
        "If the numbers still look wrong, escalate with the segment name and the approximate time you ran the export.",
      ],
    },
    {
      title: "The file doesn’t download",
      body: [
        "Check whether the browser blocked the download (pop-up/download settings).",
        "Try again in a different browser.",
        "Escalate if it’s consistently failing.",
      ],
    },
  ],
  escalateWhen: [
    "CSV export consistently fails to download.",
    "A segment appears to be misclassified (include the segment name + timestamp).",
    "You need an additional export segment or field that isn’t currently supported.",
  ],
  relatedGuides: [
    { label: "Send bulk email safely", guideId: "bulk-email" },
    { label: "Usage Analytics (Admin)", guideId: "usage-analytics" },
  ],
  relatedAdminPaths: [
    { label: "Users", path: "/users" },
    { label: "Email & Comms", path: "/email-comms" },
  ],
};
